import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  RFQ_QUOTE_CURRENCIES,
  RFQ_QUOTE_GROUPS,
  emptyRfqQuoteRow,
  rowsFromStoredOrDefaults,
  sumLineTotalsByCurrency,
  formatLineTotalsLabel,
  formatLineExtendedLabel,
  lineWeightPct,
} from "../../constants/rfqQuoteGroups";
import {
  getPublicRfq,
  getPublicRfqSubgrupos,
  submitPublicRfqQuote,
} from "../../api/Estimates/estimateRfqPublicApi";
import logo from "../../img/logos/logo-dark.svg";

const MAX_LINES = 30;

function formatDeadlineEu(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const eu = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (eu) return `${eu[1]}/${eu[2]}/${eu[3]}`;
  return s;
}

function formatUnitsEs(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString("es-ES");
}

const META_CHIP_SX = {
  supplier: {
    bgcolor: "#e8f7f3",
    color: "#067a68",
    borderColor: "rgba(0,194,163,.35)",
  },
  units: {
    bgcolor: "#eaf2fb",
    color: "#1a5f9e",
    borderColor: "rgba(33,108,182,.28)",
  },
  design: {
    bgcolor: "#eef3f2",
    color: "#2f4f48",
    borderColor: "rgba(55, 90, 84, .22)",
  },
  deadline: {
    bgcolor: "#fff3e8",
    color: "#b35a12",
    borderColor: "rgba(224,120,40,.3)",
  },
};

function MetaChip({ kind, label, value }) {
  const sx = META_CHIP_SX[kind] || META_CHIP_SX.supplier;
  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        gap: 0.15,
        px: 1.25,
        py: 0.75,
        borderRadius: 1.25,
        border: "1px solid",
        maxWidth: "100%",
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 800,
          letterSpacing: 0.06,
          textTransform: "uppercase",
          opacity: 0.78,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.84rem",
          fontWeight: 700,
          lineHeight: 1.25,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Portal público proveedor: /rfq/:token
 */
export default function EstimateRfqPortalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [rfq, setRfq] = useState(null);
  const [subgruposByGrupo, setSubgruposByGrupo] = useState({});
  const [rows, setRows] = useState(() =>
    RFQ_QUOTE_GROUPS.map((g) => emptyRfqQuoteRow(g.grupoId)),
  );
  const [leadTime, setLeadTime] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setDone(false);
      try {
        const data = await getPublicRfq(token);
        if (cancelled) return;
        setRfq(data);
        setLeadTime(data.quotedLeadTime || "");
        setNotes(data.quotedNotes || "");
        setRows(
          rowsFromStoredOrDefaults(
            data.lines || [],
            data.lines?.[0]?.moneda || "EUR",
          ),
        );
        if (data.canSubmit) {
          const subs = await getPublicRfqSubgrupos(token);
          if (cancelled) return;
          setSubgruposByGrupo(subs?.byGrupo || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              "Unable to load this quotation request",
          );
          setRfq(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const title = useMemo(() => {
    const num = rfq?.presupuesto?.numeroPresupuesto;
    return num ? `RFQ ${num}` : "Cost quotation";
  }, [rfq]);

  const formTotalLabel = useMemo(
    () => formatLineTotalsLabel(sumLineTotalsByCurrency(rows)),
    [rows],
  );
  const formTotalsByMon = useMemo(() => sumLineTotalsByCurrency(rows), [rows]);
  const submittedTotalsByMon = useMemo(
    () => sumLineTotalsByCurrency(rfq?.lines || []),
    [rfq?.lines],
  );
  const submittedTotalLabel = useMemo(
    () => formatLineTotalsLabel(sumLineTotalsByCurrency(rfq?.lines || [])),
    [rfq?.lines],
  );

  const updateRow = (key, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  };

  const removeRow = (key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const addRow = () => {
    setRows((prev) => {
      if (prev.length >= MAX_LINES) return prev;
      return [...prev, emptyRfqQuoteRow(RFQ_QUOTE_GROUPS[0].grupoId)];
    });
  };

  const buildPayloadLines = () => {
    const payloadLines = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const costRaw = String(row.coste).trim();
      if (!costRaw && !row.subgrupoId) continue;
      const cost = Number(String(row.coste).replace(",", "."));
      const gLabel =
        RFQ_QUOTE_GROUPS.find((g) => g.grupoId === row.grupoId)?.label ||
        `line ${i + 1}`;
      if (!costRaw || !Number.isFinite(cost) || cost <= 0) {
        setError(`Invalid cost in ${gLabel}`);
        return null;
      }
      if (!row.subgrupoId) {
        setError(`Select a subgroup in ${gLabel}`);
        return null;
      }
      const un = Number(String(row.un).replace(",", "."));
      if (!Number.isFinite(un) || un <= 0) {
        setError(`Invalid units in ${gLabel}`);
        return null;
      }
      payloadLines.push({
        grupoId: row.grupoId,
        subgrupoId: Number(row.subgrupoId),
        un,
        coste: cost,
        moneda: row.moneda || "EUR",
      });
    }
    if (!payloadLines.length) {
      setError("Add at least one line with subgroup and unit cost.");
      return null;
    }
    return payloadLines;
  };

  const handleSubmitClick = () => {
    setError("");
    const payloadLines = buildPayloadLines();
    if (!payloadLines) return;
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setError("");
    const payloadLines = buildPayloadLines();
    if (!payloadLines) {
      setConfirmOpen(false);
      return;
    }

    setSaving(true);
    try {
      const result = await submitPublicRfqQuote(token, {
        lines: payloadLines,
        quotedLeadTime: leadTime,
        quotedNotes: notes,
      });
      setRfq(
        result.rfq || {
          ...rfq,
          estado: "Quoted",
          canSubmit: false,
          lines: payloadLines,
        },
      );
      setDone(true);
      setConfirmOpen(false);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Could not submit quotation",
      );
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const statusBanner = () => {
    if (rfq?.estado === "Applied") {
      return (
        <Alert severity="warning">
          This quotation was applied by Stage Design and can no longer be
          edited. Below is what you submitted.
        </Alert>
      );
    }
    if (rfq?.estado === "Quoted" || done) {
      return (
        <Alert severity="success">
          Quotation submitted. It is locked for editing — Stage Design will
          review it. Below is what you sent.
        </Alert>
      );
    }
    return (
      <Alert severity="warning">
        This request can no longer be edited online (status: {rfq?.estado}).
      </Alert>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f7f6",
        py: { xs: 3, md: 5 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={2.5}>
          <Box>
            <Box
              component="img"
              src={logo}
              alt="Stage Design"
              sx={{ width: 120, mb: 1.5, display: "block" }}
            />
            <Typography variant="h5" fontWeight={800}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submit your unit costs by cost group. Stage Design will review
              before applying them to the estimate.
              {rfq?.inboxUrl ? (
                <>
                  {" "}
                  <Box
                    component="a"
                    href={rfq.inboxUrl}
                    sx={{ color: "#067a68", fontWeight: 700 }}
                  >
                    View all my quotations
                  </Box>
                </>
              ) : null}
            </Typography>
          </Box>

          {loading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          ) : error && !rfq ? (
            <Alert severity="error">{error}</Alert>
          ) : rfq ? (
            <>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "grid",
                  gridTemplateColumns: rfq.presupuesto?.imageUrl
                    ? { xs: "1fr", sm: "120px 1fr" }
                    : "1fr",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {rfq.presupuesto?.imageUrl ? (
                  <Box
                    component="img"
                    src={rfq.presupuesto.imageUrl}
                    alt="Product"
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      maxHeight: 140,
                      objectFit: "cover",
                      borderRadius: 2,
                      bgcolor: "grey.100",
                    }}
                  />
                ) : null}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    REQUEST
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {rfq.presupuesto?.nombrePresupuesto || rfq.subject || "—"}
                    </Typography>
                    {rfq.estado ? (
                      <Chip
                        size="small"
                        label={rfq.estado}
                        sx={{
                          fontWeight: 800,
                          bgcolor: "#e6faf5",
                          color: "#067a68",
                          border: "1px solid rgba(0,194,163,.28)",
                        }}
                      />
                    ) : null}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    <MetaChip
                      kind="supplier"
                      label="Supplier"
                      value={rfq.proveedorNombre || "—"}
                    />
                    {rfq.presupuesto?.units != null ? (
                      <MetaChip
                        kind="units"
                        label="Units"
                        value={formatUnitsEs(rfq.presupuesto.units)}
                      />
                    ) : null}
                    {rfq.presupuesto?.diseno ? (
                      <MetaChip
                        kind="design"
                        label="Design"
                        value={rfq.presupuesto.diseno}
                      />
                    ) : null}
                    {rfq.deadline ? (
                      <MetaChip
                        kind="deadline"
                        label="Deadline"
                        value={formatDeadlineEu(rfq.deadline)}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Box>

              {!rfq.canSubmit ? (
                <>
                  {statusBanner()}
                  {(rfq.quotedLeadTime || rfq.quotedNotes) && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Lead time
                        </Typography>
                        <Typography variant="body2">
                          {rfq.quotedLeadTime || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Notes
                        </Typography>
                        <Typography variant="body2">
                          {rfq.quotedNotes || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={800}
                      sx={{ letterSpacing: 0.04 }}
                    >
                      YOUR SUBMITTED COSTS
                    </Typography>
                    {(rfq.lines || []).length ? (
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Cost group</TableCell>
                            <TableCell>Subgroup</TableCell>
                            <TableCell align="right">Units</TableCell>
                            <TableCell align="right">Unit cost</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Weight</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rfq.lines.map((l, idx) => (
                            <TableRow key={`${l.grupoId}-${l.subgrupoId}-${idx}`}>
                              <TableCell>
                                {l.grupoLabel ||
                                  RFQ_QUOTE_GROUPS.find(
                                    (g) => g.grupoId === l.grupoId,
                                  )?.label ||
                                  `Group ${l.grupoId}`}
                              </TableCell>
                              <TableCell>
                                {l.subgrupoNombre || `#${l.subgrupoId}`}
                              </TableCell>
                              <TableCell align="right">{l.un}</TableCell>
                              <TableCell align="right">
                                {l.coste} {l.moneda || ""}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 800, color: "#067a68" }}
                              >
                                {formatLineExtendedLabel(l)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 800, color: "#067a68" }}
                              >
                                {lineWeightPct(l, submittedTotalsByMon)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ fontWeight: 800, borderBottom: 0 }}>
                              Total
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 800,
                                borderBottom: 0,
                                color: "#067a68",
                                fontSize: "1.05rem",
                              }}
                            >
                              {submittedTotalLabel}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 800, borderBottom: 0, color: "#067a68" }}
                            >
                              {Object.keys(submittedTotalsByMon).length
                                ? "100%"
                                : "—"}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No line items were stored for this quotation.
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      label="Lead time"
                      value={leadTime}
                      onChange={(e) => setLeadTime(e.target.value)}
                      placeholder="e.g. 45 days"
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    Add or remove lines as needed. Empty lines are ignored when
                    you submit.
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      px: 0.5,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: "#e8f7f3",
                      border: "1px solid rgba(0,194,163,.28)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      sx={{
                        letterSpacing: 0.06,
                        textTransform: "uppercase",
                        color: "#067a68",
                        px: 1,
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      fontWeight={800}
                      sx={{ color: "#067a68", fontSize: "1.15rem", px: 1 }}
                    >
                      {formTotalLabel}
                    </Typography>
                  </Box>

                  <Stack spacing={1.25}>
                    {rows.map((row) => {
                      const subs =
                        subgruposByGrupo[String(row.grupoId)] ||
                        subgruposByGrupo[row.grupoId] ||
                        [];
                      const selectedSub =
                        subs.find(
                          (s) =>
                            Number(s.ID_SUBGRUPO) === Number(row.subgrupoId),
                        ) || null;
                      return (
                        <Box
                          key={row.key}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1}
                            alignItems={{ md: "center" }}
                          >
                            <TextField
                              select
                              label="Cost group"
                              size="small"
                              value={row.grupoId}
                              onChange={(e) =>
                                updateRow(row.key, {
                                  grupoId: Number(e.target.value),
                                  subgrupoId: "",
                                })
                              }
                              sx={{ minWidth: { md: 180 } }}
                            >
                              {RFQ_QUOTE_GROUPS.map((g) => (
                                <MenuItem key={g.grupoId} value={g.grupoId}>
                                  {g.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Autocomplete
                              size="small"
                              options={subs}
                              value={selectedSub}
                              getOptionLabel={(o) => o?.SUBGRUPO || ""}
                              isOptionEqualToValue={(a, b) =>
                                Number(a?.ID_SUBGRUPO) ===
                                Number(b?.ID_SUBGRUPO)
                              }
                              onChange={(_e, val) =>
                                updateRow(row.key, {
                                  subgrupoId: val
                                    ? Number(val.ID_SUBGRUPO)
                                    : "",
                                })
                              }
                              renderInput={(params) => (
                                <TextField {...params} label="Subgroup" />
                              )}
                              sx={{ flex: 2, minWidth: 160 }}
                            />
                            <TextField
                              label="Units"
                              value={row.un}
                              onChange={(e) =>
                                updateRow(row.key, { un: e.target.value })
                              }
                              size="small"
                              sx={{ width: { xs: "100%", md: 90 } }}
                            />
                            <TextField
                              select
                              label="Currency"
                              value={row.moneda}
                              onChange={(e) =>
                                updateRow(row.key, {
                                  moneda: e.target.value,
                                })
                              }
                              size="small"
                              sx={{ width: { xs: "100%", md: 110 } }}
                            >
                              {RFQ_QUOTE_CURRENCIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                  {c}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="Cost"
                              value={row.coste}
                              onChange={(e) =>
                                updateRow(row.key, { coste: e.target.value })
                              }
                              size="small"
                              sx={{ width: { xs: "100%", md: 120 } }}
                            />
                            <Box
                              sx={{
                                minWidth: 80,
                                px: 1,
                                py: 0.85,
                                borderRadius: 1.25,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                textAlign: "right",
                              }}
                            >
                              <Typography
                                variant="caption"
                                display="block"
                                fontWeight={800}
                                sx={{
                                  letterSpacing: 0.05,
                                  textTransform: "uppercase",
                                  color: "text.secondary",
                                  lineHeight: 1,
                                  mb: 0.35,
                                }}
                              >
                                Total
                              </Typography>
                              <Typography fontWeight={800} sx={{ fontSize: "0.95rem" }}>
                                {formatLineExtendedLabel(row)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                minWidth: 72,
                                px: 1,
                                py: 0.85,
                                borderRadius: 1.25,
                                bgcolor: "#eef8f4",
                                border: "1px solid rgba(0,194,163,.28)",
                                textAlign: "right",
                              }}
                            >
                              <Typography
                                variant="caption"
                                display="block"
                                fontWeight={800}
                                sx={{
                                  letterSpacing: 0.05,
                                  textTransform: "uppercase",
                                  color: "#067a68",
                                  lineHeight: 1,
                                  mb: 0.35,
                                }}
                              >
                                Weight
                              </Typography>
                              <Typography
                                fontWeight={800}
                                sx={{ color: "#067a68", fontSize: "0.95rem" }}
                              >
                                {lineWeightPct(row, formTotalsByMon)}
                              </Typography>
                            </Box>
                            <Button
                              onClick={() => removeRow(row.key)}
                              disabled={rows.length <= 1}
                              size="small"
                              sx={{ minWidth: 40, color: "text.secondary" }}
                            >
                              ×
                            </Button>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      px: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={800}
                      sx={{ letterSpacing: 0.06, textTransform: "uppercase" }}
                    >
                      Total
                    </Typography>
                    <Typography
                      fontWeight={800}
                      sx={{ color: "#067a68", fontSize: "1.15rem" }}
                    >
                      {formTotalLabel}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={addRow}
                    disabled={rows.length >= MAX_LINES}
                    sx={{
                      alignSelf: "stretch",
                      borderStyle: "dashed",
                      borderColor: "#00c2a3",
                      color: "#00c2a3",
                      fontWeight: 700,
                    }}
                  >
                    + Add line
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmitClick}
                    disabled={saving}
                    sx={{
                      alignSelf: "flex-start",
                      bgcolor: "#00c2a3",
                      "&:hover": { bgcolor: "#00a88c" },
                      fontWeight: 800,
                    }}
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    Submit quotation
                  </Button>

                  <Dialog
                    open={confirmOpen}
                    onClose={() => (!saving ? setConfirmOpen(false) : null)}
                  >
                    <DialogTitle>Send quotation?</DialogTitle>
                    <DialogContent>
                      Once you submit, you will not be able to edit these costs
                      from this link. Stage Design will review what you send.
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleConfirmSubmit}
                        disabled={saving}
                        sx={{
                          bgcolor: "#00c2a3",
                          "&:hover": { bgcolor: "#00a88c" },
                          fontWeight: 800,
                        }}
                      >
                        {saving ? "Sending…" : "Yes, send quotation"}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}
